import { useLang } from "../i18n";
import { useSEO } from "../seo";
import { FadeIn } from "../components/Reveal";
import { CTAButton, PageHero, SectionLabel } from "../components/Bits";

const IMG = {
  hero: "https://images.unsplash.com/photo-1449157291145-7efd050a4d0e?q=80&w=2000&auto=format&fit=crop",
};

const copy = {
  it: {
    seoTitle: "Saldo e Stralcio Immobiliare | Nicole Srl",
    seoDesc: "Valutiamo immobili con esposizioni debitorie e procedure esecutive per individuare, quando possibile, una soluzione attraverso un'operazione immobiliare.",
    kicker: "Saldo e stralcio",
    lines: ["Una situazione complessa", "richiede una strategia."],
    sub: "Valutiamo immobili interessati da esposizioni debitorie e procedure esecutive con l'obiettivo di individuare, quando possibile, una soluzione attraverso un'operazione immobiliare.",
    processLabel: "Il percorso",
    steps: [
      { n: "01", title: "Analisi", text: "Analizziamo immobile, situazione debitoria, creditori e stato della procedura." },
      { n: "02", title: "Valutazione dell'operazione", text: "Verifichiamo se esistono le condizioni economiche e tecniche per strutturare l'acquisizione." },
      { n: "03", title: "Trattativa", text: "Viene sviluppata una strategia per raggiungere, quando possibile, accordi con i soggetti coinvolti." },
      { n: "04", title: "Acquisizione", text: "Una volta definite le condizioni e completate le verifiche necessarie, si procede alla compravendita." },
      { n: "05", title: "Nuovo inizio", text: "L'obiettivo è trasformare una situazione immobiliare complessa in un'operazione definita e sostenibile." },
    ],
    noteTitle: "Una precisazione importante",
    noteText:
      "Nicole Srl è una società immobiliare, non uno studio legale. Quando l'operazione richiede attività riservate a professionisti abilitati, il percorso viene sviluppato con il supporto dei professionisti competenti. Ogni operazione viene valutata singolarmente: nessun esito può essere garantito prima dell'analisi.",
    cta: "Richiedi una valutazione riservata",
    ctaTitle: "Raccontaci la situazione, con riservatezza.",
    ctaText: "Ogni segnalazione viene trattata con la massima discrezione. La prima valutazione non comporta alcun impegno.",
  },
  en: {
    seoTitle: "Distressed Real Estate Settlements | Nicole Srl",
    seoDesc: "We assess properties affected by debt exposure and enforcement procedures to identify, where possible, a solution through a real estate operation.",
    kicker: "Distressed settlements",
    lines: ["A complex situation", "requires a strategy."],
    sub: "We assess properties affected by debt exposure and enforcement procedures with the goal of identifying, where possible, a solution through a real estate operation.",
    processLabel: "The process",
    steps: [
      { n: "01", title: "Analysis", text: "We analyse the property, the debt situation, the creditors and the status of the procedure." },
      { n: "02", title: "Operation assessment", text: "We verify whether the economic and technical conditions exist to structure the acquisition." },
      { n: "03", title: "Negotiation", text: "A strategy is developed to reach, where possible, agreements with the parties involved." },
      { n: "04", title: "Acquisition", text: "Once conditions are defined and the necessary checks completed, the sale proceeds." },
      { n: "05", title: "A new beginning", text: "The goal is to turn a complex real estate situation into a defined and sustainable operation." },
    ],
    noteTitle: "An important clarification",
    noteText:
      "Nicole Srl is a real estate company, not a law firm. Where the operation requires activities reserved to licensed professionals, the process is developed with the support of the competent professionals. Every operation is assessed individually: no outcome can be guaranteed before the analysis.",
    cta: "Request a confidential assessment",
    ctaTitle: "Tell us about the situation, confidentially.",
    ctaText: "Every submission is handled with the utmost discretion. The first assessment involves no obligation.",
  },
};

export default function SaldoStralcio() {
  const { lang } = useLang();
  const t = copy[lang];
  useSEO({ title: t.seoTitle, description: t.seoDesc, path: "/saldo-e-stralcio", lang });

  return (
    <div data-testid="saldo-stralcio-page">
      <PageHero kicker={t.kicker} lines={t.lines} sub={t.sub} image={IMG.hero} imageAlt={lang === "it" ? "Architettura urbana — prospettiva verso l'alto" : "Urban architecture — upward perspective"} />

      <section className="mx-auto max-w-5xl px-6 py-24 md:px-10 md:py-36">
        <FadeIn>
          <SectionLabel>{t.processLabel}</SectionLabel>
        </FadeIn>
        <div className="mt-16 md:mt-24">
          {t.steps.map((s, i) => (
            <FadeIn key={s.n} delay={i * 0.05}>
              <div className="group grid items-baseline gap-3 border-b border-ink/10 py-9 md:grid-cols-12 md:gap-10 md:py-11">
                <div className="md:col-span-2">
                  <span className="font-serif text-4xl font-light text-ink/20 transition-colors duration-500 group-hover:text-bronze md:text-5xl">
                    {s.n}
                  </span>
                </div>
                <h2 className="font-serif text-2xl font-light tracking-tight md:col-span-4 md:text-3xl">{s.title}</h2>
                <p className="text-base leading-relaxed text-stone md:col-span-6">{s.text}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.1}>
          <div className="mt-20 border border-ink/15 bg-dove/40 p-8 md:p-12">
            <h2 className="text-[11px] font-medium uppercase tracking-[0.25em] text-stone">{t.noteTitle}</h2>
            <p className="mt-5 max-w-3xl text-base leading-relaxed">{t.noteText}</p>
          </div>
        </FadeIn>
      </section>

      <section className="bg-ink text-ivory">
        <div className="mx-auto max-w-7xl px-6 py-24 text-center md:px-10 md:py-36">
          <FadeIn>
            <h2 className="mx-auto max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight md:text-5xl">
              {t.ctaTitle}
            </h2>
            <p className="mx-auto mt-6 max-w-md text-base leading-relaxed text-ivory/70">{t.ctaText}</p>
            <div className="mt-10 flex justify-center">
              <CTAButton to="/contatti" variant="light" testId="saldo-cta">
                {t.cta}
              </CTAButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
